import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import './Trocas.css';

/**
 * Página exclusiva para Trocas de Nocautes
 * Separada da página de Nocautes para melhor organização
 */

const Trocas = () => {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [trocas, setTrocas] = useState([]);
  const [meusNocautes, setMeusNocautes] = useState(null);
  const [rodadasAbertas, setRodadasAbertas] = useState([]);
  const [historicoTrocas, setHistoricoTrocas] = useState([]);
  const [mostrarHistorico, setMostrarHistorico] = useState(false);
  const [trocaAtual, setTrocaAtual] = useState({
    tipo_troca: '',
    quantidade: 1,
    rodada_id: '',
  });
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [imagemAmpliada, setImagemAmpliada] = useState(null);

  const carregarDados = async () => {
    try {
      setLoading(true);
      setError('');
      const [nocautesRes, trocasRes, rodadasRes, historicoRes] = await Promise.all([
        apiClient.get(`/nocautes/jogador/${user.id}`),
        apiClient.get('/nocautes/trocas'),
        apiClient.get('/rodadas'),
        apiClient.get('/nocautes/historico'),
      ]);

      setMeusNocautes(nocautesRes.data);
      setTrocas(trocasRes.data);
      setRodadasAbertas(rodadasRes.data.filter(r => r.status === 'aberta'));
      setHistoricoTrocas(historicoRes.data);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError(err.response?.data?.error || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      carregarDados();
    }
  }, [user]);

  const handleTrocar = async (e) => {
    e.preventDefault();
    setError('');

    if (!trocaAtual.tipo_troca) {
      setError('Selecione um tipo de troca');
      return;
    }

    try {
      const payload = {
        tipo_troca: trocaAtual.tipo_troca,
      };

      // Se for troca por fichas, precisa da quantidade
      if (trocaAtual.tipo_troca === 'fichas') {
        const quantidadeInput = parseInt(trocaAtual.quantidade);
        
        // Validar que a quantidade seja pelo menos 1
        if (!quantidadeInput || quantidadeInput < 1) {
          setError('A quantidade de nocautes para trocar por fichas deve ser no mínimo 1.');
          return;
        }
        
        // Validar que o jogador tenha pelo menos 1 nocaute disponível
        if (nocautesDisponiveis < 1) {
          setError('Você precisa ter pelo menos 1 nocaute disponível para realizar trocas.');
          return;
        }
        
        // Validar que a quantidade não seja maior que os disponíveis
        if (quantidadeInput > nocautesDisponiveis) {
          setError(`Você tem apenas ${nocautesDisponiveis} nocautes disponíveis. Não é possível trocar ${quantidadeInput} nocautes.`);
          return;
        }
        
        payload.quantidade = quantidadeInput;
      }

      // Se for buyin dobrado, precisa da rodada
      if (trocaAtual.tipo_troca === 'buyin') {
        if (!trocaAtual.rodada_id) {
          setError('Selecione uma rodada aberta para usar buy-in dobrado');
          return;
        }
        payload.rodada_id = trocaAtual.rodada_id;
      }

      const response = await apiClient.post('/nocautes/trocar', payload);
      
      alert(response.data.mensagem || response.data.message || 'Troca realizada com sucesso!');
      setMostrarFormulario(false);
      setTrocaAtual({ tipo_troca: '', quantidade: 1, rodada_id: '' });
      await carregarDados(); // Recarregar dados
    } catch (err) {
      console.error('Erro ao realizar troca:', err);
      setError(err.response?.data?.error || 'Erro ao realizar troca');
    }
  };

  const selecionarTroca = (tipo) => {
    setTrocaAtual({ ...trocaAtual, tipo_troca: tipo });
    setMostrarFormulario(true);
  };

  if (loading) {
    return <div className="container">Carregando...</div>;
  }

  const nocautesDisponiveis = meusNocautes?.nocautes_dados?.disponiveis || 0;

  return (
    <div className="container trocas-page">
      <div className="page-header tolkien-trocas-theme">
        <div className="theme-header-trocas">
          <span className="tolkien-trocas-icon">🔄</span>
          <h1>Trocas de Nocautes</h1>
          <span className="tolkien-trocas-icon">🔄</span>
        </div>
        <p className="theme-subtitle-trocas">"Troque seus nocautes por benefícios especiais"</p>
        <button 
          className="btn btn-secondary btn-historico" 
          onClick={() => setMostrarHistorico(!mostrarHistorico)}
        >
          {mostrarHistorico ? 'Ocultar' : 'Ver'} Histórico de Trocas
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      {/* Card de Nocautes Disponíveis */}
      {meusNocautes && (
        <div className="nocautes-disponiveis-card">
          <div className="nocautes-stats">
            <div className="stat-item">
              <span className="stat-label">Total de Nocautes:</span>
              <span className="stat-value">{meusNocautes.nocautes_dados.total}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Disponíveis para Troca:</span>
              <span className="stat-value disponivel">{nocautesDisponiveis}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Já Utilizados em Trocas:</span>
              <span className="stat-value">{meusNocautes.nocautes_dados.usados_em_trocas || 0}</span>
            </div>
          </div>
        </div>
      )}

      {/* Histórico de Trocas */}
      {mostrarHistorico && (
        <div className="historico-trocas-card">
          <h2>📜 Histórico de Trocas</h2>
          {historicoTrocas.length === 0 ? (
            <p className="no-data">Nenhuma troca realizada ainda.</p>
          ) : (
            <div className="historico-lista">
              {historicoTrocas.map((troca) => (
                <div key={troca.id} className="historico-item">
                  <div className="historico-header">
                    <div className="historico-tipo">
                      <strong>{troca.tipo_troca}</strong>
                    </div>
                    <div className="historico-data">
                      {new Date(troca.createdAt).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                  <div className="historico-detalhes">
                    <p><strong>Nocautes usados:</strong> {troca.quantidade_nocautes}</p>
                    {troca.quantidade_fichas && (
                      <p><strong>Fichas ganhas:</strong> {troca.quantidade_fichas.toLocaleString('pt-BR')}</p>
                    )}
                    {troca.rodada && (
                      <p><strong>Rodada:</strong> {new Date(troca.rodada.data).toLocaleDateString('pt-BR')}</p>
                    )}
                    {troca.observacoes && (
                      <p><strong>Observações:</strong> {troca.observacoes}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {nocautesDisponiveis === 0 && !mostrarFormulario && (
        <div className="warning-card">
          <p>⚠️ Você não tem nocautes disponíveis para trocar.</p>
          <p>Participe de mais rodadas e dê nocautes para acumular!</p>
        </div>
      )}

      {mostrarFormulario ? (
        <div className="formulario-troca-card">
          <h2>Confirmar Troca</h2>
          <form onSubmit={handleTrocar} className="form-trocas">
            <div className="form-group">
              <label>Tipo de Troca *</label>
              <select
                value={trocaAtual.tipo_troca}
                onChange={(e) => setTrocaAtual({ ...trocaAtual, tipo_troca: e.target.value })}
                required
              >
                <option value="">Selecione uma troca...</option>
                {trocas.map(troca => (
                  <option key={troca.tipo} value={troca.tipo}>
                    {troca.descricao}
                  </option>
                ))}
              </select>
            </div>

            {trocaAtual.tipo_troca && (() => {
              const trocaSelecionada = trocas.find(t => t.tipo === trocaAtual.tipo_troca);
              if (!trocaSelecionada) return null;

              return (
                <>
                  <div className="troca-info">
                    <p><strong>Benefício:</strong> {trocaSelecionada.nome}</p>
                    <p><strong>Nocautes necessários:</strong> {trocaSelecionada.quantidade === 'variável' ? 'X (você define)' : trocaSelecionada.quantidade}</p>
                    {trocaSelecionada.quantidade !== 'variável' && nocautesDisponiveis < trocaSelecionada.quantidade && (
                      <p className="warning-text">
                        ⚠️ Você precisa de {trocaSelecionada.quantidade} nocautes, mas tem apenas {nocautesDisponiveis}
                      </p>
                    )}
                  </div>

                  {trocaAtual.tipo_troca === 'fichas' && (
                    <div className="form-group">
                      <label>Quantidade de Nocautes *</label>
                      <input
                        type="number"
                        min="1"
                        max={nocautesDisponiveis}
                        value={trocaAtual.quantidade}
                        onChange={(e) => {
                          const valor = parseInt(e.target.value) || 0;
                          // Garantir que o valor seja pelo menos 1
                          const valorFinal = valor < 1 ? 1 : (valor > nocautesDisponiveis ? nocautesDisponiveis : valor);
                          setTrocaAtual({ ...trocaAtual, quantidade: valorFinal });
                        }}
                        required
                        disabled={nocautesDisponiveis < 1}
                      />
                      <small>
                        Você receberá {trocaAtual.quantidade * 1000} fichas ({trocaAtual.quantidade} nocautes × 1.000 fichas)
                        {nocautesDisponiveis < 1 && (
                          <span className="warning-text"> ⚠️ Você precisa ter pelo menos 1 nocaute disponível.</span>
                        )}
                      </small>
                    </div>
                  )}

                  {trocaAtual.tipo_troca === 'buyin' && (
                    <div className="form-group">
                      <label>Rodada Aberta *</label>
                      <select
                        value={trocaAtual.rodada_id}
                        onChange={(e) => setTrocaAtual({ ...trocaAtual, rodada_id: e.target.value })}
                        required
                      >
                        <option value="">Selecione uma rodada...</option>
                        {rodadasAbertas.map(rodada => (
                          <option key={rodada.id} value={rodada.id}>
                            {new Date(rodada.data).toLocaleDateString('pt-BR')}
                          </option>
                        ))}
                      </select>
                      {rodadasAbertas.length === 0 && (
                        <small className="warning-text">Não há rodadas abertas no momento</small>
                      )}
                    </div>
                  )}
                </>
              );
            })()}

            <div className="form-actions">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => {
                  setMostrarFormulario(false);
                  setTrocaAtual({ tipo_troca: '', quantidade: 1, rodada_id: '' });
                }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-success"
                disabled={!trocaAtual.tipo_troca || nocautesDisponiveis < 1 || (() => {
                  const trocaSelecionada = trocas.find(t => t.tipo === trocaAtual.tipo_troca);
                  if (trocaAtual.tipo_troca === 'fichas') {
                    const quantidadeInput = parseInt(trocaAtual.quantidade) || 0;
                    return quantidadeInput < 1 || quantidadeInput > nocautesDisponiveis;
                  }
                  return trocaSelecionada && trocaSelecionada.quantidade !== 'variável' && nocautesDisponiveis < trocaSelecionada.quantidade;
                })()}
              >
                Confirmar Troca
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="trocas-grid">
          {trocas.map((troca) => {
            const podeTrocar = troca.quantidade === 'variável' || nocautesDisponiveis >= troca.quantidade;
            
            // Tentar carregar imagem da troca
            let imagemTroca = null;
            try {
              imagemTroca = require(`../assets/images/trocas/${troca.tipo}.png`);
            } catch (e) {
              // Imagem não encontrada, usar placeholder
              imagemTroca = null;
            }

            return (
              <div 
                key={troca.tipo} 
                className={`troca-card ${!podeTrocar ? 'indisponivel' : ''}`}
                onClick={() => podeTrocar && selecionarTroca(troca.tipo)}
              >
                {imagemTroca && (
                  <div 
                    className="troca-imagem"
                    onClick={(e) => {
                      e.stopPropagation();
                      setImagemAmpliada({ src: imagemTroca, nome: troca.nome });
                    }}
                    style={{ cursor: 'pointer' }}
                    title="Clique para ampliar"
                  >
                    <img src={imagemTroca} alt={troca.nome} />
                  </div>
                )}
                <div className="troca-content">
                  <h3>{troca.nome}</h3>
                  <p className="troca-descricao">{troca.descricao}</p>
                  <div className="troca-custo">
                    <span className="custo-label">Custo:</span>
                    <span className="custo-valor">
                      {troca.quantidade === 'variável' ? 'X nocautes' : `${troca.quantidade} nocautes`}
                    </span>
                  </div>
                  {!podeTrocar && (
                    <div className="troca-indisponivel">
                      <p>Você precisa de {troca.quantidade} nocautes</p>
                      <p>Você tem apenas {nocautesDisponiveis}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de Imagem Ampliada */}
      {imagemAmpliada && (
        <div 
          className="modal-imagem-ampliada"
          onClick={() => setImagemAmpliada(null)}
        >
          <div 
            className="modal-imagem-conteudo"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="modal-imagem-fechar"
              onClick={() => setImagemAmpliada(null)}
            >
              ×
            </button>
            <img src={imagemAmpliada.src} alt={imagemAmpliada.nome} />
            <p className="modal-imagem-titulo">{imagemAmpliada.nome}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Trocas;
